import apiCall, { createAxiosInstance } from "@/data/axios";

const TTS_REQUEST_TIMEOUT_MS = 180000;

export interface TalkdownFile {
  id: number;
  categoryId: number | null;
  name: string;
  description: string;
  textContent: string;
  voice: string;
  language?: string | null;
  blobPath: string;
  blobUrl: string;
  durationMs: number;
  fileSizeBytes: number;
  scopeType: string;
  scopeId: string;
  orgId: string;
  createdBy: string;
  createdByEmail: string;
  createdAt: string;
  updatedAt: string;
}

export interface SupportedLanguage {
  code: string;
  name: string;
}

export interface TalkdownCategory {
  id: number;
  parentId: number | null;
  name: string;
  scopeType: string;
  scopeId: string;
  orgId: string;
  sortOrder: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface TTSVoice {
  id: string;
  name: string;
  builtIn: boolean;
}

/** Voice style: normal or angry levels. Backend applies speed factor for non-normal. */
export type TTSStyle = "normal" | "angry" | "very_angry" | "furious";

export const TTS_STYLE_OPTIONS: { value: TTSStyle; label: string }[] = [
  { value: "normal", label: "Normal" },
  { value: "angry", label: "Angry" },
  { value: "very_angry", label: "Very angry" },
  { value: "furious", label: "Furious" },
];

export async function generateTTS(
  text: string,
  voice: string = "alba",
  language: string = "en",
  autoTranslate?: boolean,
  style: TTSStyle = "normal"
): Promise<Blob> {
  const { createAxiosInstance } = await import("@/data/axios");
  const instance = createAxiosInstance("POST", undefined, TTS_REQUEST_TIMEOUT_MS);
  const body: Record<string, unknown> = {
    text,
    voice,
    language: language || "en",
    ...(autoTranslate === undefined ? {} : { autoTranslate }),
  };
  if (style && style !== "normal") {
    body.style = style;
  }
  const res = await instance.post(
    "/alert-lens/tts/generate",
    body,
    { responseType: "arraybuffer", headers: { Accept: "audio/wav" } }
  );
  return new Blob([res.data], { type: "audio/wav" });
}

export interface TTSTranslatePreviewResponse {
  translatedText: string;
  sourceLanguage: string;
  targetLanguage: string;
}

export async function translateTTSPreview(
  text: string,
  language: string
): Promise<TTSTranslatePreviewResponse> {
  const res = await apiCall<TTSTranslatePreviewResponse>(
    "POST",
    "/alert-lens/tts/translate-preview",
    { text, language }
  );
  return (res as any)?.data ?? res;
}

export async function generateAndSaveTTS(params: {
  text: string;
  voice: string;
  name: string;
  description?: string;
  language?: string;
  autoTranslate?: boolean;
  style?: TTSStyle;
  scopeType: string;
  scopeId: string;
  orgId: string;
  categoryId?: number;
  userId?: string;
  userEmail?: string;
}): Promise<TalkdownFile> {
  const res = await apiCall<TalkdownFile>(
    "POST",
    "/alert-lens/tts/generate-and-save",
    params,
    undefined,
    TTS_REQUEST_TIMEOUT_MS
  );
  return (res as any)?.data ?? res;
}

export interface TTSVoicesResponse {
  voices: TTSVoice[];
  modelLoaded: boolean;
  multilingualLoaded?: boolean;
  supportedLanguages?: SupportedLanguage[];
}

export async function fetchTTSVoices(): Promise<TTSVoicesResponse> {
  const res = await apiCall<TTSVoicesResponse>("GET", "/alert-lens/tts/voices");
  return (res as any)?.data ?? res;
}

export async function fetchTalkdownLibrary(params: {
  orgId: string;
  scopeType?: string;
  scopeId?: string;
  categoryId?: number;
}): Promise<TalkdownFile[]> {
  const qs = new URLSearchParams();
  qs.set("orgId", params.orgId);
  if (params.scopeType) qs.set("scopeType", params.scopeType);
  if (params.scopeId) qs.set("scopeId", params.scopeId);
  if (params.categoryId) qs.set("categoryId", String(params.categoryId));
  const res = await apiCall<TalkdownFile[]>(
    "GET",
    `/alert-lens/talkdown/library?${qs.toString()}`
  );
  return (res as any)?.data ?? res;
}

export async function fetchTalkdownCategories(params: {
  orgId: string;
  scopeType?: string;
  scopeId?: string;
}): Promise<TalkdownCategory[]> {
  const qs = new URLSearchParams();
  qs.set("orgId", params.orgId);
  if (params.scopeType) qs.set("scopeType", params.scopeType);
  if (params.scopeId) qs.set("scopeId", params.scopeId);
  const res = await apiCall<TalkdownCategory[]>(
    "GET",
    `/alert-lens/talkdown/categories?${qs.toString()}`
  );
  return (res as any)?.data ?? res;
}

export async function createTalkdownCategory(params: {
  name: string;
  parentId?: number;
  scopeType: string;
  scopeId: string;
  orgId: string;
  createdBy?: string;
}): Promise<TalkdownCategory> {
  const res = await apiCall<TalkdownCategory>(
    "POST",
    "/alert-lens/talkdown/categories",
    params
  );
  return (res as any)?.data ?? res;
}

export async function updateTalkdownCategory(
  id: number,
  params: { name?: string; parentId?: number; sortOrder?: number }
): Promise<TalkdownCategory> {
  const res = await apiCall<TalkdownCategory>(
    "PUT",
    `/alert-lens/talkdown/categories/${id}`,
    params
  );
  return (res as any)?.data ?? res;
}

export async function deleteTalkdownCategory(id: number): Promise<void> {
  await apiCall("DELETE", `/alert-lens/talkdown/categories/${id}`);
}

export async function updateTalkdownItem(
  id: number,
  params: {
    name?: string;
    description?: string;
    categoryId?: number | null;
    scopeType?: string;
    scopeId?: string;
  }
): Promise<TalkdownFile> {
  const res = await apiCall<TalkdownFile>(
    "PUT",
    `/alert-lens/talkdown/library/${id}`,
    params
  );
  return (res as any)?.data ?? res;
}

export async function deleteTalkdownItem(id: number): Promise<void> {
  await apiCall("DELETE", `/alert-lens/talkdown/library/${id}`);
}

export async function uploadTalkdownAudio(params: {
  file: File;
  name: string;
  description?: string;
  textContent?: string;
  voice?: string;
  language?: string;
  scopeType: string;
  scopeId: string;
  orgId: string;
  categoryId?: number;
  userId?: string;
  userEmail?: string;
}): Promise<TalkdownFile> {
  const formData = new FormData();
  formData.append("audioFile", params.file);
  formData.append("name", params.name);
  formData.append("scopeType", params.scopeType);
  formData.append("scopeId", params.scopeId);
  formData.append("orgId", params.orgId);
  if (params.description) formData.append("description", params.description);
  if (params.textContent) formData.append("textContent", params.textContent);
  if (params.voice) formData.append("voice", params.voice);
  if (params.language) formData.append("language", params.language);
  if (params.categoryId !== undefined) {
    formData.append("categoryId", String(params.categoryId));
  }
  if (params.userId) formData.append("userId", params.userId);
  if (params.userEmail) formData.append("userEmail", params.userEmail);

  const instance = createAxiosInstance("POST");
  const res = await instance.post("/alert-lens/talkdown/library/upload", formData);
  return (res as any)?.data ?? res;
}

/** Fetch library item audio as a playable blob URL (uses app auth; caller must revoke the URL when done). */
export async function fetchTalkdownAudioUrl(id: number): Promise<string> {
  const instance = createAxiosInstance("GET");
  const res = await instance.get(`/alert-lens/talkdown/library/${id}/audio`, {
    responseType: "arraybuffer",
    headers: { Accept: "audio/wav" },
  });
  const blob = new Blob([res.data], { type: "audio/wav" });
  return URL.createObjectURL(blob);
}
